// Where each Fleet Live panel sits (#6779, prototype `:3522-3539`): four zones, Top,
// Middle, Side and Bottom, and the order panels stack in. A PER-VIEWER preference through
// the one `localStorage` wrapper (`local-pref.ts`), under the prototype's own key
// `fleet-live-layout-v2`, as `{ lay: { core, deck, topo }, order: [3 keys] }`.
//
// A STORED VALUE IS READ PER FIELD: an unknown zone falls back to that panel's default zone
// (the others keep theirs), and an order that is not the three keys, each once, falls back
// to the default order. Anything that is not an object reads as the default. Pure but for
// `readLayout` / `writeLayout`.

import { z } from 'zod';
import { FLEET_LIVE_PANELS, type FleetLivePanel } from './fleet-live-vis';
import { readLocalPref, writeLocalPref } from './local-pref';

export const LAYOUT_KEY = 'fleet-live-layout-v2';

export const LIVE_ZONES = ['top', 'middle', 'side', 'bottom'] as const;
export type LiveZone = (typeof LIVE_ZONES)[number];

export type LiveLayout = {
  readonly lay: Readonly<Record<FleetLivePanel, LiveZone>>;
  readonly order: readonly FleetLivePanel[];
};

/** Core Intelligence on top, the Command Deck in the middle, the Topology on the side. */
export const DEFAULT_LAYOUT: LiveLayout = {
  lay: { core: 'top', deck: 'middle', topo: 'side' },
  order: ['core', 'deck', 'topo'],
};

const isZone = (v: unknown): v is LiveZone =>
  typeof v === 'string' && (LIVE_ZONES as readonly string[]).includes(v);
const isPanel = (v: unknown): v is FleetLivePanel =>
  typeof v === 'string' && (FLEET_LIVE_PANELS as readonly string[]).includes(v);

/** Any stored value, as a layout: per field, falling back to the default. */
export function parseLayout(raw: unknown): LiveLayout {
  if (typeof raw !== 'object' || raw === null) return DEFAULT_LAYOUT;
  const { lay, order } = raw as { lay?: unknown; order?: unknown };
  const stored = typeof lay === 'object' && lay !== null ? (lay as Record<string, unknown>) : {};
  const zoneOf = (k: FleetLivePanel): LiveZone => {
    const z = stored[k];
    return isZone(z) ? z : DEFAULT_LAYOUT.lay[k];
  };
  const keys = Array.isArray(order) ? order : [];
  const valid =
    keys.length === FLEET_LIVE_PANELS.length &&
    keys.every(isPanel) &&
    new Set(keys).size === FLEET_LIVE_PANELS.length;
  return {
    lay: { core: zoneOf('core'), deck: zoneOf('deck'), topo: zoneOf('topo') },
    order: valid ? (keys as FleetLivePanel[]) : DEFAULT_LAYOUT.order,
  };
}

export function readLayout(): LiveLayout {
  return parseLayout(readLocalPref<unknown>(LAYOUT_KEY, z.unknown(), null));
}

export function writeLayout(layout: LiveLayout): void {
  writeLocalPref(LAYOUT_KEY, layout);
}

/** `panel` moved to `zone`, at the end of the order (so at the end of that zone). */
export function movePanel(layout: LiveLayout, panel: FleetLivePanel, zone: LiveZone): LiveLayout {
  if (layout.lay[panel] === zone) return layout;
  return {
    lay: { ...layout.lay, [panel]: zone },
    order: [...layout.order.filter((k) => k !== panel), panel],
  };
}

/** The default layout ("Reset layout"). */
export function resetLayout(): LiveLayout {
  return DEFAULT_LAYOUT;
}

/** The panels in `zone`, in stacking order. */
export function panelsIn(layout: LiveLayout, zone: LiveZone): FleetLivePanel[] {
  return layout.order.filter((k) => layout.lay[k] === zone);
}
